import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { t } from '../locales/nl';
import { useFormChange } from '../contexts/useFormChange';
import { useFormLeaveConfirmation } from '../hooks/useFormLeaveConfirmation';

interface FormLayoutProps {
  title: string;
  children: React.ReactNode;
  currentSection?: string;
}

const FormLayout: React.FC<FormLayoutProps> = ({ title, children, currentSection }) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const { hasUnsavedChanges, resetFormState } = useFormChange();

  // Use the leave confirmation hook
  const { safeNavigate } = useFormLeaveConfirmation({
    hasUnsavedChanges,
    onBeforeLeave: () => {
      resetFormState();
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolled]);

  return (
    <div className="min-h-screen bg-neutral-50 pb-8">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex flex-col">
            <div className="flex items-center">
              <button 
                onClick={() => safeNavigate('/')}
                className="p-2 -ml-2 rounded-full hover:bg-neutral-100 transition-colors"
                aria-label={t.goBack}
              >
                <ChevronLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold ml-2">{title}</h1>
            </div>
            {currentSection && hasScrolled && (
              <p className="text-sm text-neutral-600 ml-10 animate-fade-in">
                {currentSection}
              </p>
            )}
          </div>
        </div>
      </header>
      
      <main className="page-container fade-in">
        {children}
      </main>
    </div>
  );
};

export default FormLayout;